import type { Locale } from '../types'

/**
 * Client Review — Revision 03 (request #12): new Monthly / Annual Recap.
 * Highest-priority layer of `t()`.
 */
export const review03Strings: Record<Locale, Record<string, string>> = {
  'pt-BR': {
    'eng.recap.title': 'Retrospectiva',
    'eng.recap.subtitle': 'Sua jornada no período, em momentos.',
    'eng.recap.month': 'Mensal',
    'eng.recap.year': 'Anual',
    'eng.recap.storyAria': 'Momento {n} de {total}',
    'eng.recap.prev': 'Anterior',
    'eng.recap.next': 'Próximo',
    'eng.recap.toShare': 'Ir para o card final',

    'eng.recap.intro.month': 'Seu mês no BEELYV',
    'eng.recap.intro.year': 'Seu ano no BEELYV',
    'eng.recap.intro.body': 'Cada dia que você cuidou de si ficou guardado aqui. Vamos olhar juntos.',

    'eng.recap.bee.overline': 'Seu tipo de abelha do período',
    'eng.recap.bee.overlineYear': 'Sua abelha do ano',
    'eng.recap.bee.basis': 'Baseado na constância dos seus hábitos do Daily Check neste período.',
    'eng.recap.bee.note': 'Um retrato divertido do período — não é diagnóstico, rótulo permanente nem ranking.',

    'eng.recap.hive.overline': 'Sua Colmeia cresceu',
    'eng.recap.hive.start': 'Início do período',
    'eng.recap.hive.end': 'Fim do período',
    'eng.recap.hive.cells': '{n} células',
    'eng.recap.hive.delta': '{from} → {to} células',
    'eng.recap.hive.evolved': 'Sua Colmeia evoluiu de {from} para {to}',
    'eng.recap.hive.same': 'Sua Colmeia seguiu crescendo como {stage}',

    'eng.recap.consistency.overline': 'Sua constância',
    'eng.recap.consistency.days': '{n} de {total} dias com Daily Check',
    'eng.recap.consistency.streak': 'Maior sequência: {n} dias',
    'eng.recap.consistency.avg': 'Life Score médio: {n}',

    'eng.recap.habit.overline': 'Hábito mais constante',
    'eng.recap.habit.tied': '{a} e {b} empataram no topo',
    'eng.recap.habit.ratio': 'Concluído em {n}% dos dias',

    'eng.recap.activity.overline': 'Atividade principal',
    'eng.recap.activity.body': 'Foi a atividade que mais apareceu na sua rotina.',

    'eng.recap.community.overline': 'Na comunidade',
    'eng.recap.community.posts': '{n} publicações',
    'eng.recap.community.challenges': '{n} desafios concluídos',

    'eng.recap.social.overline': 'Quem esteve com você',
    'eng.recap.social.month': '{name} foi quem mais esteve por perto neste mês',
    'eng.recap.social.year': '{name} foi quem mais esteve por perto neste ano',
    'eng.recap.social.count': '{n} interações nas suas publicações',
    'eng.recap.social.note': 'Conta só curtidas e comentários no que você publicou.',

    'eng.recap.pct.overline': 'Sua consistência na comunidade',
    'eng.recap.pct.month': 'Você esteve entre os {n}% mais consistentes da comunidade {community} neste mês.',
    'eng.recap.pct.year': 'Você esteve entre os {n}% mais consistentes da comunidade {community} neste ano.',
    'eng.recap.pct.insufficient': 'Ainda não há dados suficientes para comparação nesta comunidade.',
    'eng.recap.pct.choose': 'Escolha a comunidade',
    'eng.recap.pct.note': 'Contexto pela constância do Daily Check. Sem nomes, posições ou pontuações de outras pessoas.',

    'eng.recap.milestones.overline': 'Grandes marcos do ano',

    'eng.recap.share.overline': 'Seu card final',
    'eng.recap.share.hive': 'Colmeia',
    'eng.recap.share.options': 'O que aparece no card',
    'eng.recap.share.opt.streak': 'Maior sequência',
    'eng.recap.share.opt.challenges': 'Desafios concluídos',
    'eng.recap.share.opt.activity': 'Atividade principal',
    'eng.recap.share.opt.community': 'Contexto da comunidade',
    'eng.recap.share.opt.communityOff': 'Disponível quando a comunidade escolhida tiver dados suficientes.',
    'eng.recap.share.never':
      'Peso, composição corporal, biomarcadores, humor e dados sensíveis de saúde nunca entram no card.',
    'eng.recap.share.cta': 'Compartilhar retrospectiva',
    'eng.recap.share.cardLabel': 'Card de retrospectiva para compartilhar',

    'eng.recap.reward.title': 'Bônus de compartilhamento',
    'eng.recap.reward.eligible': 'Elegível após um compartilhamento confirmado: +{n} Bee de bônus, uma vez. Valor de exemplo.',
    'eng.recap.reward.verification':
      'A confirmação real depende dos recursos disponíveis no dispositivo e será implementada no Marco 3.',
    'eng.recap.reward.demoLabel': 'Modo de demonstração · somente protótipo',
    'eng.recap.reward.simulate': 'Simular compartilhamento concluído',
    'eng.recap.reward.demoNote': 'Demonstra o bônus. Não existe no produto final.',
    'eng.recap.reward.granted': '+{n} Bee de bônus de compartilhamento. Bee de bônus não conta para liberar o Post.',
    'eng.recap.reward.already': 'Bônus já recebido nesta retrospectiva. Compartilhar de novo não gera mais Bee.',
    'eng.recap.reward.optional': 'Opcional — a retrospectiva está completa mesmo sem compartilhar.',
    'eng.recap.reward.saveNote': 'Salvar no aparelho não gera bônus.',
  },
  'en-US': {
    'eng.recap.title': 'Recap',
    'eng.recap.subtitle': 'Your journey in the period, moment by moment.',
    'eng.recap.month': 'Monthly',
    'eng.recap.year': 'Annual',
    'eng.recap.storyAria': 'Moment {n} of {total}',
    'eng.recap.prev': 'Previous',
    'eng.recap.next': 'Next',
    'eng.recap.toShare': 'Go to the final card',

    'eng.recap.intro.month': 'Your month on Beelyv',
    'eng.recap.intro.year': 'Your year on Beelyv',
    'eng.recap.intro.body': 'Every day you took care of yourself is kept here. Let’s look back together.',

    'eng.recap.bee.overline': 'Your Bee type for the period',
    'eng.recap.bee.overlineYear': 'Your Bee of the year',
    'eng.recap.bee.basis': 'Based on how consistently you completed your Daily Check habits in this period.',
    'eng.recap.bee.note': 'A playful snapshot of the period — not a diagnosis, a permanent label or a ranking.',

    'eng.recap.hive.overline': 'Your Hive grew',
    'eng.recap.hive.start': 'Start of period',
    'eng.recap.hive.end': 'End of period',
    'eng.recap.hive.cells': '{n} cells',
    'eng.recap.hive.delta': '{from} → {to} cells',
    'eng.recap.hive.evolved': 'Your Hive evolved from {from} to {to}',
    'eng.recap.hive.same': 'Your Hive kept growing as a {stage}',

    'eng.recap.consistency.overline': 'Your consistency',
    'eng.recap.consistency.days': '{n} of {total} days with a Daily Check',
    'eng.recap.consistency.streak': 'Longest streak: {n} days',
    'eng.recap.consistency.avg': 'Average Life Score: {n}',

    'eng.recap.habit.overline': 'Most consistent habit',
    'eng.recap.habit.tied': '{a} and {b} tied at the top',
    'eng.recap.habit.ratio': 'Completed on {n}% of days',

    'eng.recap.activity.overline': 'Main activity',
    'eng.recap.activity.body': 'The activity that showed up most in your routine.',

    'eng.recap.community.overline': 'In the community',
    'eng.recap.community.posts': '{n} posts',
    'eng.recap.community.challenges': '{n} challenges completed',

    'eng.recap.social.overline': 'Who was with you',
    'eng.recap.social.month': '{name} was there for you the most this month',
    'eng.recap.social.year': '{name} was there for you the most this year',
    'eng.recap.social.count': '{n} interactions on your posts',
    'eng.recap.social.note': 'Counts only likes and comments on what you published.',

    'eng.recap.pct.overline': 'Your consistency in the community',
    'eng.recap.pct.month': 'You were among the top {n}% most consistent in the {community} community this month.',
    'eng.recap.pct.year': 'You were among the top {n}% most consistent in the {community} community this year.',
    'eng.recap.pct.insufficient': 'There isn’t enough data for a comparison in this community yet.',
    'eng.recap.pct.choose': 'Choose the community',
    'eng.recap.pct.note': 'Context based on Daily Check consistency. No names, positions or scores of other people.',

    'eng.recap.milestones.overline': 'Major milestones of the year',

    'eng.recap.share.overline': 'Your final card',
    'eng.recap.share.hive': 'Hive',
    'eng.recap.share.options': 'What appears on the card',
    'eng.recap.share.opt.streak': 'Longest streak',
    'eng.recap.share.opt.challenges': 'Challenges completed',
    'eng.recap.share.opt.activity': 'Main activity',
    'eng.recap.share.opt.community': 'Community context',
    'eng.recap.share.opt.communityOff': 'Available when the chosen community has enough data.',
    'eng.recap.share.never':
      'Weight, body composition, biomarkers, mood and sensitive health data never go on the card.',
    'eng.recap.share.cta': 'Share recap',
    'eng.recap.share.cardLabel': 'Recap card to share',

    'eng.recap.reward.title': 'Share bonus',
    'eng.recap.reward.eligible': 'Eligible after a confirmed share: +{n} bonus Bee, once. Sample value.',
    'eng.recap.reward.verification':
      'Real confirmation depends on what the device supports and will be built in Marco 3.',
    'eng.recap.reward.demoLabel': 'Demo mode · prototype only',
    'eng.recap.reward.simulate': 'Simulate completed share',
    'eng.recap.reward.demoNote': 'Demonstrates the bonus. Not part of the final product.',
    'eng.recap.reward.granted': '+{n} Bee share bonus. Bonus Bee never counts toward the Post unlock.',
    'eng.recap.reward.already': 'Bonus already received for this recap. Sharing again adds no more Bee.',
    'eng.recap.reward.optional': 'Optional — the recap is complete without sharing.',
    'eng.recap.reward.saveNote': 'Saving to your device doesn’t earn the bonus.',
  },
}
